import { createFileRoute } from "@tanstack/react-router";
import { PriorityServiceAreaPage } from "@/components/PriorityServiceAreaPage";
import { seoHead, serviceSchema, localBusinessSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";

const FAQS = [
  { q: "How much is a clutch replacement in Poplar?", a: "Most cars: £550–£950 fitted including parts and labour. Dual-mass flywheel cars cost more. We'll quote exactly before any work." },
  { q: "How long does it take?", a: "Most clutch jobs are a single day. Complex layouts (4WD, dual-mass) can run into a second day. We'll tell you when you book." },
  { q: "How do I know my clutch is failing?", a: "Common signs: high biting point, slipping under load, juddering as you pull away, or a burning smell going up hills. Come in for a free check." },
  { q: "Do you replace the dual-mass flywheel too?", a: "We recommend it on cars that have one — replacing the clutch without the flywheel often means another labour-heavy job within months." },
];

export const Route = createFileRoute("/poplar/clutch-repairs")({
  head: () => seoHead({
    path: "/poplar/clutch-repairs",
    title: "Clutch Replacement in Poplar, E14 — Honest Quotes | Woodseer Autos",
    description: "Clutch replacement and repair in Poplar, E14. Single and dual-mass flywheels. Honest quote, quality parts, fast turnaround.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Poplar", path: "/areas/poplar" },
      { name: "Clutch Repairs", path: "/poplar/clutch-repairs" },
    ],
    jsonLd: [
      localBusinessSchema({ serviceCoverage: ["Poplar"] }),
      serviceSchema({
        name: "Clutch Replacement in Poplar",
        serviceType: "Clutch repair and replacement",
        description: "Full clutch and flywheel replacement for cars and light vans in Poplar, E14. Quality parts, honest quotes.",
        areaServed: "Poplar, London",
        url: "https://woodseerautos.co.uk/poplar/clutch-repairs",
      }),
      faqSchema(FAQS) as Record<string, unknown>,
    ],
  }),
  component: () => (
    <PriorityServiceAreaPage
      areaSlug="poplar"
      areaName="Poplar"
      serviceSlug="clutch-repairs"
      serviceName="Clutch Repairs"
      parentServiceSlug="mechanical-repairs"
      hero="Slipping clutch, juddering pull-away or a high biting point? Our Poplar workshop replaces clutch kits, dual-mass flywheels and slave/master cylinders with clear quotes upfront."
      localBenefit="Diagnosis and quote before any strip-down work begins"
      whatsIncluded={[
        "Full clutch kit (plate, cover, release bearing)",
        "Dual-mass flywheel replacement where fitted",
        "Slave and master cylinder inspection",
        "Gearbox oil top-up or replacement",
        "Road test before handover",
        "12-month parts and labour warranty",
      ]}
      commonIssues={[
        "Biting point at the top of the pedal",
        "Slipping in 4th/5th gear under load",
        "Burning smell after hill climbs",
        "Juddering when pulling away",
        "Heavy or notchy pedal feel",
        "Clutch pedal sticks to the floor",
      ]}
      process={[
        { step: "Diagnosis", desc: "We confirm the clutch is the issue (not just oil leaks or linkage)." },
        { step: "Quote", desc: "Parts, flywheel option, labour — all written down before you commit." },
        { step: "Replace", desc: "Usually a day's work. Quality OE-equivalent or genuine parts." },
        { step: "Road test", desc: "Tested under load before you collect — and warrantied for 12 months." },
      ]}
      whyUs={[
        "Quotes that include the flywheel — no second job later",
        "12-month warranty on parts and labour",
        "Quality brands (LuK, Sachs, Valeo) as standard",
        "Honest diagnosis — sometimes it's not the clutch",
      ]}
      faqs={FAQS}
    />
  ),
});
